import { Chip, type ChipProps } from '@mui/material';
import { palette } from '../theme/palette';

export type StatusKind =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'emergency'
  | 'warning'
  | 'success'
  /**
   * Factual, not evaluative — a state that is neither good nor bad and should
   * not compete for attention. A superseded document is the case that needed it:
   * it is history, and colouring it as a warning would read as a problem.
   */
  | 'neutral';

/**
 * The one place a status kind becomes colour.
 *
 * Exported because the editable status pill on the enrolments roster draws the
 * same states as a trigger rather than a chip. It resolved them through MUI's
 * palette instead, so `warning` was one amber in a chip and a different amber
 * two columns to its left — the same status, twice, in one row.
 */
export const statusStyles: Record<StatusKind, { bg: string; fg: string; label: string }> = {
  neutral: { bg: palette.border.onCream, fg: palette.text.muted, label: 'NEUTRAL' },
  pending: { bg: palette.semantic.pendingTint, fg: palette.semantic.pending, label: 'PENDING' },
  approved: { bg: palette.semantic.successTint, fg: palette.semantic.success, label: 'APPROVED' },
  rejected: {
    bg: palette.semantic.emergencyTint,
    fg: palette.semantic.emergency,
    label: 'REJECTED',
  },
  emergency: {
    bg: palette.semantic.emergencyTint,
    fg: palette.semantic.emergency,
    label: 'EMERGENCY',
  },
  warning: { bg: palette.semantic.warningTint, fg: palette.semantic.warning, label: 'WARNING' },
  success: { bg: palette.semantic.successTint, fg: palette.semantic.success, label: 'SUCCESS' },
};

export interface StatusChipProps extends Omit<ChipProps, 'color'> {
  status: StatusKind;
}

export function StatusChip({ status, label, sx, ...rest }: StatusChipProps) {
  const s = statusStyles[status];
  return (
    <Chip
      label={label ?? s.label}
      size="small"
      sx={{ backgroundColor: s.bg, color: s.fg, fontSize: '0.7rem', height: 22, ...sx }}
      {...rest}
    />
  );
}
